import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Card,
  CardContent,
  CardActionArea,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Link,
} from '@mui/material';
import { School as CoachIcon, DirectionsRun as AthleteIcon } from '@mui/icons-material';
import TermsOfServiceDialog from './TermsOfServiceDialog';
import PrivacyPolicyDialog from './PrivacyPolicyDialog';

interface RoleSelectionDialogProps {
  open: boolean;
  onRoleSelected: (role: 'coach' | 'athlete') => void;
  loading?: boolean;
}

const RoleSelectionDialog: React.FC<RoleSelectionDialogProps> = ({
  open,
  onRoleSelected,
  loading = false,
}) => {
  const [selectedRole, setSelectedRole] = useState<'coach' | 'athlete'>('athlete');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);

  const handleSubmit = () => {
    if (!acceptedTerms) {
      return;
    }
    onRoleSelected(selectedRole);
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
      onClose={() => {}} // Prevent closing by clicking backdrop
    >
      <DialogTitle>
        <Typography variant="h5" component="div" align="center" gutterBottom>
          Welcome to Waka Running!
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary">
          Please select your role to get started
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Choose your role:</strong> This determines what features and permissions you'll have in the app.
            You can always contact support to change this later if needed.
          </Alert>
        </Box>

        <RadioGroup
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value as 'coach' | 'athlete')}
          sx={{ mb: 2 }}
        >
          <Card 
            variant="outlined" 
            sx={{ 
              mb: 2,
              border: selectedRole === 'coach' ? '2px solid' : '1px solid',
              borderColor: selectedRole === 'coach' ? 'primary.main' : 'divider',
              backgroundColor: selectedRole === 'coach' ? 'action.hover' : 'background.paper',
            }}
          >
            <CardActionArea onClick={() => setSelectedRole('coach')}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <CoachIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    Coach
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Create and manage teams, track athlete progress, and analyze team performance.
                </Typography>
                <Typography variant="caption" color="primary" sx={{ ml: 4, fontWeight: 'bold' }}>
                  Perfect for: Coaches, trainers, team leaders
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card 
            variant="outlined"
            sx={{ 
              border: selectedRole === 'athlete' ? '2px solid' : '1px solid',
              borderColor: selectedRole === 'athlete' ? 'primary.main' : 'divider',
              backgroundColor: selectedRole === 'athlete' ? 'action.hover' : 'background.paper',
            }}
          >
            <CardActionArea onClick={() => setSelectedRole('athlete')}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <AthleteIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6" component="div">
                    Athlete
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Join teams, log runs, track personal goals, and earn points.
                </Typography>
                <Typography variant="caption" color="primary" sx={{ ml: 4, fontWeight: 'bold' }}>
                  Perfect for: Runners, athletes, team members
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </RadioGroup>

        <FormControlLabel
          control={
            <Checkbox
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              disabled={loading}
              required
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{' '}
              <Link
                component="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowTermsDialog(true);
                }}
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link
                component="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPrivacyDialog(true);
                }}
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Privacy Policy
              </Link>
            </Typography>
          }
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          size="large"
          fullWidth
          disabled={loading || !acceptedTerms}
          sx={{ py: 1.5 }}
        >
          {loading ? 'Setting up your account...' : 'Continue'}
        </Button>
      </DialogActions>

      <TermsOfServiceDialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
      />

      <PrivacyPolicyDialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
      />
    </Dialog>
  );
};

export default RoleSelectionDialog;
